/**
 * 分拣机/交叉带接口 spec（驱动 SorterApiToolPage 的参数表单与请求拼装）。
 * 加接口只需在此加一条 spec，无需改页面逻辑。
 */

export type SorterFieldType = 'text' | 'number' | 'array' | 'enum' | 'timestamp' | 'sorter';

export type SorterApiGroup = 'crossBelt' | 'rapidScan' | 'smallParcelScanner';

export interface SorterField {
    name: string;
    label: string;
    type: SorterFieldType;
    required: boolean;
    /** 示例值（text/number）或数组默认值示意 */
    example?: string;
    /** enum 类型的可选值 */
    enumValues?: string[];
}

export interface SorterApiSpec {
    key: string;
    label: string;
    method: 'GET' | 'POST';
    path: string;
    group: SorterApiGroup;
    fields: SorterField[];
}

/**
 * 环境预置（仅 baseUrl，可在页面手改）。
 * 签名凭证 appKey/appSecret 不在此、也不写死前端，由后端 getSorterApiCredentials 查库(channel api_version=4)下发、用户下拉选。
 */
export interface SorterEnvPreset {
    key: string;
    label: string;
    baseUrl: string;
}

export const SORTER_ENV_PRESETS: SorterEnvPreset[] = [
    { key: 'test10', label: 'test10', baseUrl: 'https://swiftx.test10.tita.ltd' },
];

const TIMESTAMP_FIELD: SorterField = {
    name: 'requestTimestamp',
    label: 'requestTimestamp（秒，自动填当前时间，可刷新/改）',
    type: 'timestamp',
    required: true,
};

export const SORTER_APIS: SorterApiSpec[] = [
    // ── 交叉带 / DWS ──────────────────────────────────────────────
    {
        key: 'getChuteConfig',
        // 后端同时支持 GET/POST；工具用 POST 携带 JSON body（浏览器 GET 不能带 body）
        label: '获取格口配置 getChuteConfig',
        method: 'POST',
        path: '/api/v4/openapi/getChuteConfig',
        group: 'crossBelt',
        fields: [
            { name: 'machineCode', label: 'machineCode 分拣机编码', type: 'sorter', required: true },
            TIMESTAMP_FIELD,
        ],
    },
    {
        key: 'crossBeltScan',
        label: '交叉带扫描 crossBeltScan',
        method: 'POST',
        path: '/api/v4/openapi/crossBeltScan',
        group: 'crossBelt',
        fields: [
            { name: 'machineCode', label: 'machineCode 分拣机编码', type: 'sorter', required: true },
            { name: 'barcodes', label: 'barcodes 条码（多值）', type: 'array', required: true, example: 'SWX037190000015234589' },
            { name: 'lengthCm', label: 'lengthCm 长(cm)', type: 'number', required: false, example: '10.1' },
            { name: 'widthCm', label: 'widthCm 宽(cm)', type: 'number', required: false, example: '25.7' },
            { name: 'heightCm', label: 'heightCm 高(cm)', type: 'number', required: false, example: '20.5' },
            { name: 'weightKg', label: 'weightKg 重(kg)', type: 'number', required: false, example: '2.191' },
            TIMESTAMP_FIELD,
        ],
    },
    {
        key: 'crossBeltDropParcel',
        label: '交叉带落格 crossBeltDropParcel',
        method: 'POST',
        path: '/api/v4/openapi/crossBeltDropParcel',
        group: 'crossBelt',
        fields: [
            { name: 'machineCode', label: 'machineCode 分拣机编码', type: 'sorter', required: true },
            { name: 'trackingNumber', label: 'trackingNumber 运单号', type: 'text', required: true, example: 'SWX784390000000365027' },
            { name: 'expectChuteCodes', label: 'expectChuteCodes 期望格口（多值）', type: 'array', required: true, example: '1' },
            { name: 'chuteCode', label: 'chuteCode 实际格口', type: 'text', required: true, example: '1' },
            TIMESTAMP_FIELD,
        ],
    },
    {
        key: 'crossBeltLockChute',
        label: '交叉带锁格口 crossBeltLockChute',
        method: 'POST',
        path: '/api/v4/openapi/crossBeltLockChute',
        group: 'crossBelt',
        fields: [
            { name: 'machineCode', label: 'machineCode 分拣机编码', type: 'sorter', required: true },
            { name: 'chuteCode', label: 'chuteCode 格口', type: 'text', required: true, example: '1' },
            { name: 'trackingNumbers', label: 'trackingNumbers 运单号（多值）', type: 'array', required: true, example: 'SWX784390000000365027' },
            TIMESTAMP_FIELD,
        ],
    },
    {
        key: 'crossBeltChuteUpdate',
        label: '交叉带格口状态更新 crossBeltChuteUpdate',
        method: 'POST',
        path: '/api/v4/openapi/crossBeltChuteUpdate',
        group: 'crossBelt',
        fields: [
            { name: 'machineCode', label: 'machineCode 分拣机编码', type: 'sorter', required: true },
            { name: 'chuteCode', label: 'chuteCode 格口', type: 'text', required: true, example: '1' },
            { name: 'chuteStatus', label: 'chuteStatus 格口状态', type: 'enum', required: true, example: 'NORMAL', enumValues: ['NORMAL', 'FULL', 'LOCKED'] },
            TIMESTAMP_FIELD,
        ],
    },
    // ── 狂扫 ──────────────────────────────────────────────────────
    {
        key: 'rapidScan',
        label: '狂扫扫码 rapidScan',
        method: 'POST',
        path: '/api/v4/openapi/rapidScan',
        group: 'rapidScan',
        fields: [
            { name: 'sortingMachineCode', label: 'sortingMachineCode 分拣机编码', type: 'sorter', required: true },
            { name: 'trackingNumber', label: 'trackingNumber 运单号', type: 'text', required: true, example: 'SWX784390000000365027' },
            { name: 'weightKg', label: 'weightKg 重(kg)', type: 'number', required: false, example: '2.5' },
            { name: 'lengthCm', label: 'lengthCm 长(cm)', type: 'number', required: false, example: '30.0' },
            { name: 'widthCm', label: 'widthCm 宽(cm)', type: 'number', required: false, example: '25.0' },
            { name: 'heightCm', label: 'heightCm 高(cm)', type: 'number', required: false, example: '20.0' },
            TIMESTAMP_FIELD,
        ],
    },
    // ── 小件快手 ──────────────────────────────────────────────────
    {
        key: 'sortingMachineScan',
        label: '小件快手扫码 sortingMachineScan',
        method: 'POST',
        path: '/api/v4/openapi/sortingMachineScan',
        group: 'smallParcelScanner',
        fields: [
            { name: 'sortingMachineCode', label: 'sortingMachineCode 分拣机编码', type: 'sorter', required: true },
            { name: 'trackingNumber', label: 'trackingNumber 运单号', type: 'text', required: true, example: 'SWX784390000000365027' },
            { name: 'weightKg', label: 'weightKg 重(kg)', type: 'number', required: true, example: '2.5' },
            { name: 'lengthCm', label: 'lengthCm 长(cm)', type: 'number', required: false, example: '30.0' },
            { name: 'widthCm', label: 'widthCm 宽(cm)', type: 'number', required: false, example: '25.0' },
            { name: 'heightCm', label: 'heightCm 高(cm)', type: 'number', required: false, example: '20.0' },
            TIMESTAMP_FIELD,
        ],
    },
    {
        key: 'getShippingLabelPdf',
        label: '获取面单PDF getShippingLabelPdf',
        method: 'POST',
        path: '/api/v4/openapi/getShippingLabelPdf',
        group: 'smallParcelScanner',
        fields: [
            { name: 'sortingMachineCode', label: 'sortingMachineCode 分拣机编码', type: 'sorter', required: true },
            { name: 'trackingNumber', label: 'trackingNumber 运单号', type: 'text', required: true, example: 'SWX784390000000365027' },
            TIMESTAMP_FIELD,
        ],
    },
    {
        key: 'confirmRelabel',
        label: '换单二次确认 confirmRelabel',
        method: 'POST',
        path: '/api/v4/openapi/confirmRelabel',
        group: 'smallParcelScanner',
        fields: [
            { name: 'logId', label: 'logId 换单日志ID（getShippingLabelPdf 返回）', type: 'number', required: true, example: '123456' },
            { name: 'scannedTrackingNumber', label: 'scannedTrackingNumber 扫描到的新单号', type: 'text', required: true, example: 'SWX784390000000365027' },
            TIMESTAMP_FIELD,
        ],
    },
];
